'use client';

import type { ProductVariantSearchResultDto } from '@/apis/backend/generated/models';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/context/organization-context';
import { usePaginatedProducts } from '@/hooks/use-paginated-products';

import { Search as SearchIcon, ShoppingBag as ProductIcon } from '@mui/icons-material';
import {
  Box,
  Grid,
  Card,
  Chip,
  Alert,
  Stack,
  Dialog,
  Button,
  Checkbox,
  TextField,
  CardMedia,
  Typography,
  DialogTitle,
  CardContent,
  ToggleButton,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  ToggleButtonGroup,
} from '@mui/material';

/** Layout types for product grids */
export type ProductGridLayout = '1' | '1x2' | '2x2';

/** Result type including layout selection */
export interface ProductPickerResult {
  products: ProductVariantSearchResultDto[];
  layout: ProductGridLayout;
}

interface ProductPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with products (legacy) or with products + layout if showLayoutSelector is true */
  onSelect: (products: ProductVariantSearchResultDto[], layout?: ProductGridLayout) => void;
  selectedProducts: ProductVariantSearchResultDto[];
  /** When true, only allows selecting a single product (legacy mode) */
  singleSelect?: boolean;
  /** When true, shows layout selector (1, 1x2, 2x2) and enables multi-select based on layout */
  showLayoutSelector?: boolean;
  /** Externally set max products -- hides layout selector and uses this value directly */
  maxProducts?: number;
}

export function ProductPickerModal({
  open,
  onClose,
  onSelect,
  selectedProducts,
  singleSelect = false,
  showLayoutSelector = false,
  maxProducts: maxProductsProp,
}: ProductPickerModalProps) {
  const { t } = useTranslation('emailGen');
  const [searchQuery, setSearchQuery] = useState('');
  const [localSelected, setLocalSelected] = useState<ProductVariantSearchResultDto[]>(selectedProducts);
  const [selectedLayout, setSelectedLayout] = useState<ProductGridLayout>('1');
  const { activeOrganization, activeClient } = useOrganization();

  // Calculate max products based on layout
  const getMaxProducts = (layout: ProductGridLayout): number => {
    switch (layout) {
      case '1': return 1;
      case '1x2': return 2;
      case '2x2': return 4;
      default: return 1;
    }
  };

  const maxProducts = maxProductsProp != null
    ? maxProductsProp
    : showLayoutSelector
      ? getMaxProducts(selectedLayout)
      : (singleSelect ? 1 : Infinity);

  // Browse the catalog, or search it once the query is long enough. Paginated:
  // the grid scrolls to the end of the catalog rather than stopping at a fixed
  // first page.
  const {
    products,
    isSearching,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
  } = usePaginatedProducts({
    orgId: activeOrganization?.id || '',
    clientId: activeClient?.id || '',
    searchQuery,
    enabled: open,
  });

  // Reset local selection when modal opens
  const handleOpen = () => {
    setLocalSelected(selectedProducts);
    setSelectedLayout('1');
  };

  // Handle layout change
  const handleLayoutChange = (_: React.MouseEvent<HTMLElement>, newLayout: ProductGridLayout | null) => {
    if (newLayout) {
      setSelectedLayout(newLayout);
      // Trim selection if it exceeds new max
      const newMax = getMaxProducts(newLayout);
      setLocalSelected((prev) => prev.slice(0, newMax));
    }
  };

  // Handle product selection toggle
  const handleToggleProduct = (product: ProductVariantSearchResultDto) => {
    setLocalSelected((prev) => {
      const isSelected = prev.some((p) => p.variantId === product.variantId);
      if (isSelected) {
        return prev.filter((p) => p.variantId !== product.variantId);
      }
      // Check if we've reached the max
      if (prev.length >= maxProducts) {
        // Replace the last selection if at max
        if (maxProducts === 1) {
          return [product];
        }
        // Otherwise don't add more
        return prev;
      }
      return [...prev, product];
    });
  };

  // Check if a product is selected
  const isProductSelected = (product: ProductVariantSearchResultDto) => localSelected.some((p) => p.variantId === product.variantId);

  // Handle confirm selection
  const handleConfirm = () => {
    onSelect(localSelected, showLayoutSelector ? selectedLayout : undefined);
    onClose();
  };

  // Get product image URL
  const getProductImageUrl = (product: ProductVariantSearchResultDto) => {
    if (product.productImages && product.productImages.length > 0) {
      return product.productImages[0].src;
    }
    return null;
  };

  // Format price (handle both string and number since PostgreSQL decimal returns as string)
  const formatPrice = (price?: number | string | null) => {
    if (price === undefined || price === null) return '';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '';
    return `$${numPrice.toFixed(2)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      onTransitionEnter={handleOpen}
      PaperProps={{
        sx: { height: '80vh', maxHeight: 700 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ProductIcon color="primary" />
          <Typography variant="h6">
            {(showLayoutSelector || maxProductsProp != null) ? t('productPickerModal.titleMulti', 'Select Products') : (singleSelect ? t('productPickerModal.titleSingle', 'Select Product') : t('productPickerModal.titleMulti', 'Select Products'))}
          </Typography>
          {(showLayoutSelector || !singleSelect) && localSelected.length > 0 && (
            <Chip
              label={showLayoutSelector
                ? t('productPickerModal.selectedOfMax', '{{count}}/{{max}} selected', { count: localSelected.length, max: maxProducts })
                : t('productPickerModal.selectedCount', '{{count}} selected', { count: localSelected.length })}
              color="primary"
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Layout Selector (hidden when maxProducts is externally set) */}
        {showLayoutSelector && maxProductsProp == null && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('productPickerModal.layout', 'Layout')}
            </Typography>
            <ToggleButtonGroup
              value={selectedLayout}
              exclusive
              onChange={handleLayoutChange}
              size="small"
              fullWidth
            >
              <ToggleButton value="1" sx={{ flex: 1 }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 24, height: 24, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                  </Box>
                  <Typography variant="caption">{t('productPickerModal.layoutSingle', 'Single')}</Typography>
                </Stack>
              </ToggleButton>
              <ToggleButton value="1x2" sx={{ flex: 1 }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 20, height: 24, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                    <Box sx={{ width: 20, height: 24, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                  </Box>
                  <Typography variant="caption">{t('productPickerModal.layout1x2', '1x2')}</Typography>
                </Stack>
              </ToggleButton>
              <ToggleButton value="2x2" sx={{ flex: 1 }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 12, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                      <Box sx={{ width: 16, height: 12, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 12, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                      <Box sx={{ width: 16, height: 12, bgcolor: 'action.selected', borderRadius: 0.5 }} />
                    </Box>
                  </Box>
                  <Typography variant="caption">{t('productPickerModal.layout2x2', '2x2')}</Typography>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Search Input */}
        <TextField
          fullWidth
          placeholder={t('productPickerModal.searchPlaceholder', 'Search products by name or SKU...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
          autoFocus
        />

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('productPickerModal.errorSearch', 'Failed to search products. Please try again.')}
          </Alert>
        )}

        {/* No Results */}
        {!isLoading && products.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {isSearching
                ? t('productPickerModal.noResultsSearch', 'No products found for "{{query}}"', { query: searchQuery })
                : t('productPickerModal.noProductsAvailable', 'No products available')}
            </Typography>
          </Box>
        )}

        {/* Product Grid */}
        {products.length > 0 && (
          <Grid container spacing={2}>
            {products.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.variantId}>
                <Card
                  onClick={() => handleToggleProduct(product)}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isProductSelected(product) ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: isProductSelected(product)
                        ? 'primary.main'
                        : 'grey.300',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {getProductImageUrl(product) ? (
                      <CardMedia
                        component="img"
                        height={140}
                        image={getProductImageUrl(product) || ''}
                        alt={product.productTitle}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <ProductIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                    )}
                    <Checkbox
                      checked={isProductSelected(product)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'white',
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'white' },
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleProduct(product)}
                    />
                  </Box>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      title={product.productTitle}
                    >
                      {product.productTitle}
                    </Typography>
                    {product.variantTitle && product.variantTitle !== 'Default Title' && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {product.variantTitle}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      {product.variantPrice && (
                        <Typography variant="body2" fontWeight={500} color="primary">
                          {formatPrice(product.variantPrice)}
                        </Typography>
                      )}
                      {product.variantCompareAtPrice &&
                        Number(product.variantCompareAtPrice) > Number(product.variantPrice || 0) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                          >
                            {formatPrice(product.variantCompareAtPrice)}
                          </Typography>
                        )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Infinite-scroll sentinel: loads the next page as it nears the viewport */}
        {hasNextPage && (
          <Box ref={sentinelRef} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            {isFetchingNextPage && <CircularProgress size={24} />}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={localSelected.length === 0 || (showLayoutSelector && localSelected.length < maxProducts)}
        >
          {showLayoutSelector
            ? (localSelected.length === maxProducts
                ? (maxProducts === 1
                    ? t('productPickerModal.addProduct', 'Add Product')
                    : t('productPickerModal.addProducts', 'Add {{count}} Products', { count: maxProducts }))
                : t('productPickerModal.selectMore', 'Select {{count}} more', { count: maxProducts - localSelected.length }))
            : (singleSelect
                ? (localSelected.length > 0
                    ? t('productPickerModal.confirmSelectProduct', 'Select Product')
                    : t('productPickerModal.promptSelectProduct', 'Select a Product'))
                : (localSelected.length > 0
                    ? t('productPickerModal.addSelectedProducts', 'Add ({{count}}) Products', { count: localSelected.length })
                    : t('productPickerModal.addProductsEmpty', 'Add Products')))
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
